/* Action-level permission helpers.
 *
 * localStorage "permissions" holds the /modules/sidebar response for the
 * logged-in user: each module (resource) carries an `actions` array with the
 * granted action slugs, e.g. ["view", "update", "resend-invite"].
 *
 * Older cached payloads used { view, create } booleans — those are mapped to
 * their equivalent actions so stale caches keep working.
 *
 * Policy: fail-open. If a module can't be resolved (unknown slug, empty
 * cache), the UI renders the control and the backend remains the enforcer.
 * When the module IS present, its actions list is authoritative.
 */

export const getPermissionModules = () => {
  try {
    return JSON.parse(localStorage.getItem("permissions")) || [];
  } catch {
    return [];
  }
};

const grantedActions = (module) => {
  if (Array.isArray(module?.actions)) return module.actions;
  const legacy = [];
  if (module?.view) legacy.push("view");
  if (module?.create) legacy.push("create");
  return legacy;
};

/* Find a module by its slug (preferred) or its url. */
export const findModule = (slugOrUrl) =>
  getPermissionModules().find(
    (m) => m.slug === slugOrUrl || m.url === slugOrUrl,
  );

/* can("users", "resend-invite") → is the action granted on that resource? */
export const can = (slugOrUrl, action, fallback = true) => {
  const module = findModule(slugOrUrl);
  if (!module) return fallback;
  return grantedActions(module).includes(action);
};

export const canView = (slugOrUrl) => can(slugOrUrl, "view");

/* For raw module objects (e.g. while rendering the sidebar list). */
export const moduleHasView = (module) =>
  grantedActions(module).includes("view");
